/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

/* eslint-disable max-lines, max-statements */

// TODO #169 Refaktorovat adaptéry a device.

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import noop from 'lodash/noop';

import config from '../config';
import AnalyticsMigrator from '../core/analytics/analytics-migrator';
import { commandTypeArray } from '../core/command/command-type';
import CommunicatorCommand, {
  parseCommandDateTimeParam
} from '../core/communicator/command/communicator-command';
import CommunicatorFirmware from '../core/communicator/firmware/communicator-firmware';
import { QueueIdEnum } from '../core/communicator/queue/communicator-queue';
import ContentInfoParser from '../core/content/info/content-info-parser';
import ErrorName from '../core/error/error-name';
import { errorWithLevelWarning } from '../core/error/error-with-level';
import { errorWithName } from '../core/error/error-with-name';
import exceptionResource from '../core/exception/exception-resource';
import { fetchWithTimeout } from '../core/fetch/fetch';
import FileSystemSpaceDetector from '../core/file/system/file-system-space-detector';
import FileSystemUtil from '../core/file/system/file-system-util';
import Numeric from '../core/numeric/numeric';
import PromiseUtil from '../core/promise/promise-util';
import Queue from '../core/queue/queue';
import {
  assertIsServerDeviceId,
  isServerDeviceId
} from '../core/session/session-attrs';
import { migrate } from '../core/store/js-data-store-migrator';
import UnixTimestamp from '../core/unix-timestamp/unix-timestamp';
import { never } from '../util.type';

/**
 * @import { Device, EventTypes } from '.'
 * @import { IAnalyticsTransporter } from '../core/analytics'
 * @import AnalyticsSender from '../core/analytics/analytics-sender'
 * @import AnalyticsStore from '../core/analytics/analytics-store'
 * @import AnalyticsTracker from '../core/analytics/analytics-tracker'
 * @import { AppManager } from '../core/app'
 * @import BasePathResolver from '../core/basepath/basepath'
 * @import { Byte } from '../core/byte'
 * @import Cleaner from '../core/cleaner/cleaner'
 * @import { CommandType, RawCommand } from '../core/command'
 * @import Communicator from '../core/communicator/communicator'
 * @import ContentSettingsStore from '../core/content/content-settings-store'
 * @import { DownloadCallbacks } from '../core/content/downloader'
 * @import ContentDownloader from '../core/content/downloader/content-downloader'
 * @import { ContentInfo } from '../core/content/info/content-info'
 * @import { ErrorWithLevel, Warning } from '../core/error/error-with-level'
 * @import FileSystemUnitFinder from '../core/file-system-unit-finder/file-system-unit-finder'
 * @import { FileSystemManager } from '../core/file/system'
 * @import { FirmwareInfo, FirmwareManager, ManufacturerModel } from '../core/firmware'
 * @import { HeartbeatFetched } from '../core/heartbeat'
 * @import { Logger } from '../core/logger/logger'
 * @import { LogicalUnitMapper } from '../core/logical-unit-mapper'
 * @import ReplacerPower from '../core/replacer/power/replacer-power'
 * @import Replacer from '../core/replacer/replacer'
 * @import { ReportInfo } from '../core/report'
 * @import ReportManager from '../core/report/report-manager'
 * @import { Session } from '../core/session/session'
 * @import { StartupManager } from '../core/startup/startup-manager'
 * @import StatusInfoDialogController from '../core/status-info-dialog/status-info-dialog-controller'
 * @import Store from '../core/store/store'
 * @import Updater from '../core/updater/updater'
 * @import { Url } from '../core/url'
 * @import { IVariablesNamespaceRegistry } from '../core/variables'
 * @import ViewerManager from '../core/viewer/manager/viewer-manager'
 * @import { ViewerRegistry } from '../core/viewer/registry'
 */

/**
 * Maximální počet příkazů, které se zpracují v rámci heartbeatu
 * v teoreticky nekonečné smyčce zpracování fronty příkazů.
 */
const maxCommandsPerHeartbeat = 20;

// TODO #44 Dopsat E2E testy pro update. Jak nastavovat na serveru?
/**
 * Zařízení, které posktytuje služby pomocí libovolného adaptéru.
 * Do budoucna z něj bude vyčleněna třída Client, která bude poskytovat část jeho rozhraní přes MessageChannel.
 * @implements {Device}
 */
export default class DeviceStateful {
  /**
   * Vytvoří instanci.
   * @param {AnalyticsSender} analyticsSender
   * @param {AnalyticsStore} analyticsStore
   * @param {AnalyticsTracker} analyticsTracker
   * @param {IAnalyticsTransporter} analyticsTransporter
   * @param {AppManager} appManager
   * @param {BasePathResolver} basePathResolver
   * @param {Cleaner} cleaner
   * @param {Communicator} communicator
   * @param {ContentSettingsStore} contentSettingsStore
   * @param {ContentDownloader} contentDownloader
   * @param {FileSystemManager} fileSystemManager
   * @param {FileSystemUnitFinder} fileSystemUnitFinder
   * @param {FirmwareManager} firmwareManager
   * @param {Logger} logger
   * @param {LogicalUnitMapper} logicalUnitMapper
   * @param {Replacer} replacer
   * @param {ReplacerPower} replacerPower
   * @param {ReportManager} reportManager
   * @param {Session} session
   * @param {StartupManager} startupManager
   * @param {StatusInfoDialogController} statusInfoDialogController
   * @param {Store} store
   * @param {UnixTimestamp} unixTimestamp
   * @param {Updater} updater
   * @param {IVariablesNamespaceRegistry} variablesNamespaceRegistry
   * @param {ViewerManager} viewerManager
   * @param {ViewerRegistry} viewerRegistry
   */
  constructor(
    analyticsSender,
    analyticsStore,
    analyticsTracker,
    analyticsTransporter,
    appManager,
    basePathResolver,
    cleaner,
    communicator,
    contentSettingsStore,
    contentDownloader,
    fileSystemManager,
    fileSystemUnitFinder,
    firmwareManager,
    logger,
    logicalUnitMapper,
    replacer,
    replacerPower,
    reportManager,
    session,
    startupManager,
    statusInfoDialogController,
    store,
    unixTimestamp,
    updater,
    variablesNamespaceRegistry,
    viewerManager,
    viewerRegistry
  ) {
    this.analyticsSender = analyticsSender;
    this.analyticsStore = analyticsStore;
    this.analyticsTracker = analyticsTracker;
    this.analyticsTransporter = analyticsTransporter;
    this.appManager = appManager;
    this.basePathResolver = basePathResolver;
    this.cleaner = cleaner;
    this.contentSettingsStore = contentSettingsStore;
    this.communicator = communicator;
    this.contentDownloader = contentDownloader;
    this.fileSystemManager = fileSystemManager;
    this.fileSystemUnitFinder = fileSystemUnitFinder;
    this.firmwareManager = firmwareManager;
    this.logger = logger;
    this.logicalUnitMapper = logicalUnitMapper;
    this.replacer = replacer;
    this.replacerPower = replacerPower;
    this.reportManager = reportManager;
    this.session = session;
    this.startupManager = startupManager;
    this.statusInfoDialogController = statusInfoDialogController;
    this.store = store;
    this.unixTimestamp = unixTimestamp;
    this.updater = updater;
    this.variablesNamespaceRegistry = variablesNamespaceRegistry;
    this.viewerManager = viewerManager;
    this.viewerRegistry = viewerRegistry;

    this.analyticsInitialized = false;
    this.applyNewContentMutex = new Queue();
    this.commandByTypeMutexes = new Map(
      commandTypeArray.map((commandType) => [commandType, new Queue()])
    );
    /**
     * Obecně mutex pro čtení fronty příkazů. De facto mutex pro
     * {@link enqueCommandsByTypeUntilEmpty}.
     */
    this.commandQueueMutex = new Queue();
    /**
     * Aktuální obsah, který se na zařízení přehrává.
     * `null` indikuje, že není k dispozici žádný obsah (např. zastavení obsahu před čištěním, žádné ContentSettings v indexeddb).
     * @type {ContentInfo | null}
     */
    this.contentInfo = null;
    /** @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();
    /**
     * Ovladač zrušení probíhajících zpracování příkazů.
     */
    this.commandQueueAbortController = new AbortController();
    /** @type {DownloadCallbacks} */
    this.downloadCallbacks = {
      onStart: (total) => this.emitter.emit('downloadStart', total),
      onProgress: (progress) => this.emitter.emit('downloadProgress', progress),
      onComplete: () => this.emitter.emit('downloadComplete')
    };
    this.stateful = true;

    // Navázání naslouchání na instanci.
    this.onCleanerBeforeRemoveSomeAssets =
      this.onCleanerBeforeRemoveSomeAssets.bind(this);
    this.onError = this.onError.bind(this);
    this.onHeartbeatFetched = this.onHeartbeatFetched.bind(this);

    this.analyticsSender.on('error', this.onError);
    this.contentDownloader.on(
      'cleanerBeforeRemoveSomeAssets',
      this.onCleanerBeforeRemoveSomeAssets
    );
  }

  /**
   * Spustí kontinuální synchronizaci obsahu zařízení se spárovaným serverem.
   * @private
   * @returns {Promise<void>} Vyřeší se po dokončení první synchronizace.
   */
  async connect() {
    const { communicator, session } = this;
    assertIsServerDeviceId(session);
    const { deviceId, serverUrl } = session;
    // Naslouchání událostem.
    communicator.on('error', this.onError);
    communicator.on('heartbeatFetched', this.onHeartbeatFetched);
    await communicator.connect(serverUrl, deviceId);
  }

  /**
   * Zkontroluje, jestli je na interním úložišti dostatek místa.
   * @param {Byte} size Požadovaná velikost, kterou je potřeba mít
   * volnou na úložišti.
   * @returns {Promise<boolean>} Příznak, zda je dostatek místa.
   */
  async hasInternalStorageSpace(size) {
    const logicalUnit = await FileSystemUtil.find(
      this.fileSystemManager,
      this.fileSystemUnitFinder,
      this.logicalUnitMapper,
      'internal'
    );
    if (!logicalUnit) {
      this.logger.warn('No internal file system unit found.');
      return false;
    }
    const fileSystemUnit = await this.logicalUnitMapper.toFileSystemUnit(
      logicalUnit
    );
    return await FileSystemSpaceDetector.detect(fileSystemUnit, size);
  }

  /** @type {Device['destroy']} */
  async destroy() {
    // Zastavení obsahu.
    await this.viewerManager.setContentInfo(null);

    this.analyticsSender.off('error', this.onError);
    const { communicator } = this;
    communicator.off('error', this.onError);
    communicator.off('heartbeatFetched', this.onHeartbeatFetched);
    // Zrušení stahovače souborů.
    this.contentDownloader.off(
      'cleanerBeforeRemoveSomeAssets',
      this.onCleanerBeforeRemoveSomeAssets
    );
    // Zrušení případného probíhajícího zpracování příkazů.
    this.commandQueueAbortController.abort();
  }

  /**
   * Ve smyčce vytáhne z fronty příkazů na serveru příkazy, naparsuje je
   * a zařadí do fronty podle typu pro paralelní zpracování, ale nečeká
   * na dokončení příkazů.
   *
   * Pro omezení maximálního počtu vytahovaných/zpracovávaných příkazů slouží
   * {@link maxCommandsPerHeartbeat}.
   *
   * Při síťové chybě se přeruší bez vrácené chyby stávající dávka, aby nedošlo
   * k DOS útoku při opakovaných pokusech.
   * @private
   */
  async enqueCommandsByTypeUntilEmpty() {
    await this.commandQueueMutex.enqueue(async () => {
      const signal = this.commandQueueAbortController.signal;
      if (signal.aborted) {
        return;
      }

      // Pokud se session nezmění pod rukama, tak může assert být mimo smyčku.
      assertIsServerDeviceId(this.session);

      let fetchedCommands = 0;
      // TODO: A případné tranzitivní předávání abortSignal, např. do fetchů, atd.
      while (!signal.aborted && fetchedCommands < maxCommandsPerHeartbeat) {
        /** @type {Awaited<ReturnType<Communicator['fetchCommandJson']>>} */
        let json;
        try {
          // eslint-disable-next-line no-await-in-loop
          json = await this.communicator.fetchCommandJson(this.session);
          // Při prázdné frontě se smyčka přeruší.
          if (!json) {
            return;
          }
        } catch (error) {
          // Při chybě stahování přerušíme zpracování příkazů v tomto
          // heartbeatu, abychom nedělali opakování ihned po sobě, jelikož
          // jsme i tak chráněno pomocí maxCommandsPerHeartbeat, ale
          // typicky při výpadku internetu opakování nepomůže a když tak
          // se zkusí v dalším heartbeatu.
          this.onError(/** @type {Warning} */ (error));
          return;
        }

        fetchedCommands += 1;

        /** @type {ReturnType<typeof CommunicatorCommand['parse']>} */
        let command;
        try {
          command = CommunicatorCommand.parse(json);
        } catch (error) {
          // Pokud nejde příkaz naparsovat, tak zalogujeme chybu a pokračujeme.
          this.onError(/** @type {Error} */ (error));
          // eslint-disable-next-line no-continue
          continue;
        }

        // Zámerne nečakáme na vykonanie príkazu, aby sa mohli vykonávať
        // viaceré súčasne. Pozor, jelikož nečekáme na výsledek, tak
        // musíme ošetřit chybu zde!
        this.executeCommand(command).catch(this.onError);
      }
    });
  }

  /**
   * Vykoná paralelně surový příkaz (pokud je podporovaný - jinak tiše ignoruje)
   * ve frontách podle typů příkazů a počká na jeho dokončení.
   * @param {RawCommand} command
   * @throws {Error}
   * @private
   */
  async executeCommand(command) {
    // Nepodporované commandy ticho ignorujeme pomocí `?.`.
    await this.commandByTypeMutexes
      .get(/** @type {CommandType} */ (command.type))
      ?.enqueue(async () => {
        // TODO Refaktorovat s využitím polymorfismu místo switch.
        // - Vytvořit rozhraní Command s metodou #execute().
        // - Implementovat rozhraní pro každý typ příkazu. V konstruktoru služby, které příkaz provedou.
        // - Vytvořit třídu CommandFactory s metodou #create(commandEnum): Command.
        //   Nebo továrna pro každý typ? Nebo registr příkazů s #register(commandEnum, commandFactory)?
        const { params } = command;
        const type = /** @type {CommandType} */ (command.type);
        switch (type) {
          // Toto je specifiký command, kdy nechceme, aby docházelo ke kolizím
          // se stahováním obsahu (proto se používá stejný mutex). Např. stahování
          // obsahu trvá dlouho a mezitím je ze serveru odeslán příkaz na smazání
          // obsahu. V ideálním případě by se stahování mělo zastavit a ihned se
          // spustit čištění úložiště.
          // TODO: Čekání na výsledek fronty může zapříčinit opoždění odeslání
          // status info, pokud se stahuje obsah a byl přijat příkaz na čištění
          // úložiště, a odeslání status info.
          case 'deleteContent':
            return await this.applyNewContentMutex.enqueue(() =>
              this.stopContent().then(() => this.cleaner.cleanAll())
            );
          case 'deleteUnusedContent':
            return await this.applyNewContentMutex.enqueue(async () => {
              if (isNil(this.contentInfo)) {
                return;
              }
              await this.cleaner.clean(this.contentInfo);
              await this.executeReportCommandWithoutError();
            });
          case 'identify':
            return this.statusInfoDialogController.open();
          case 'info':
            assertIsServerDeviceId(this.session);
            // Získání informací do reportu a jeho odeslání na server.
            return await this.communicator.report(
              this.session.serverUrl,
              this.session.deviceId,
              await this.getReportInfo(this.contentInfo)
            );
          case 'lcd':
            if (params.length < 1) {
              throw new TypeError(
                `LCD command: expected at least 1 param, got ${params.length}`
              );
            }
            return await this.shutdownWithWakeUpCommand(params[0]);
          case 'restart':
            return await this.replacerPower.reboot();
          case 'shutdown':
            if (params.length < 2) {
              throw new TypeError(
                `Shutdown command: expected 2 params, got ${params.length}`
              );
            }
            switch (params[1]) {
              case 'S5':
                // U S5 se ignoruje první parametr čas probuzení.
                return await this.replacerPower.shutdown();
              case 'S3':
                return await this.shutdownWithWakeUpCommand(params[0]);
              // Ostatní módy jsou ignorované.
              default:
                return;
            }
          case 'upgradeFirmware':
            // Při úspěchu se zablokuje navždy, aby se případný další command nezačal
            // zpracovávat v době restartu. Taky se zablokuje navždy,
            // pokud neumíme zjistit chybu updatu.
            return await this.upgradeFirmware();
          default:
            // Kontrola před zařazením do fronty by měla zajistit, že se sem nedostane neznámý příkaz.
            never(type);
        }
      });
  }

  /**
   * Vykoná příkaz report ve frontě příkazů a počká na jeho dokončení.
   * Případné chyby jsou zalogovány a tím pádem nikdy nevrhne.
   * @private
   */
  async executeReportCommandWithoutError() {
    await this.executeCommand({ type: 'info', params: [] }).catch(this.onError);
  }

  /**
   * Zjistí informace o zařízení.
   * @private
   * @param {ContentInfo | null} contentInfo
   * @returns {Promise<ReportInfo>} Informace o zařízení.
   */
  getReportInfo(contentInfo) {
    return this.reportManager
      .getInfo(contentInfo)
      .catch((error) => {
        let errorWithLevel = /** @type {ErrorWithLevel} */ (error);
        // Podľa #534 sa posiela NotSupportedError vyvolaný .getVolume()
        if (errorWithLevel.name === ErrorName.NotSupportedError) {
          errorWithLevel = errorWithLevelWarning(error);
        }
        this.onError(errorWithLevel);
        return {};
      })
      .then((reportInfo) => ({
        ...reportInfo,
        version: this.session.version ?? undefined
      }));
  }

  /** @type {Device['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {Device['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na požadavek na zastavení přehrávání obsahu při čištění místa na disku.
   * @private
   * @returns {void}
   */
  onCleanerBeforeRemoveSomeAssets() {
    this.stopContent().catch(this.onError);
  }

  /**
   * Reakce na chybu.
   * @private
   * @param {Error} error Chybový objekt.
   */
  onError(error) {
    if (error.name === ErrorName.NotSupportedError) {
      return this.logger.warn(error);
    }

    return this.logger.error(error);
  }

  /**
   * Reakce na doručení heartbeatu z Ki-Wi Serveru.
   * @private
   * @param {HeartbeatFetched} heartbeatFetched Příchozí heartbeat.
   * @returns {Promise<void>}
   */
  async onHeartbeatFetched(heartbeatFetched) {
    try {
      // TODO #626: Nenahradit za assertServerDeviceId, která může vrhnout?
      // A je to zde vhodné, aby to nenadělalo více škody než užitku?
      if (!isServerDeviceId(this.session)) {
        // Tento případ může nastast pouze chybou při vývoji, kdy dojde k porušení předpokladů.
        // Jinak je zajištěno, že zde deviceId a serverUrl budou vyplněné.
        return;
      }
      const { deviceId, serverUrl } = this.session;
      // Předání heartbeat info s metadaty pro výpočet offsetu.
      this.setOffsetFromHeartbeat(heartbeatFetched);
      const { heartbeatInfo } = heartbeatFetched;

      // Zařazení do fronty příkazů z heartbeat. Ostatní typy front nejsou
      // podporovány a zpracovávány.
      if (heartbeatInfo.nonEmptyQueues.has(QueueIdEnum.COMMAND)) {
        // Záměrně se nepoužívá await, aby dávka maxCommandsPerHeartbeat commandů
        // (resp. jejich fetch, jelikož se uvnitř nečeká na zpracování) zbytečně
        // neblokovala. Zároveň je zde potřeba (možná duplicitně) ošetřit chyby.
        this.enqueCommandsByTypeUntilEmpty().catch(this.onError);
      }

      const updateResult = await this.updater.updateVersion(
        heartbeatInfo.version
      );
      if (updateResult) {
        // V případě, že došlo k aktualizaci aplikace, tak je nežádoucí aby se
        // pokračovalo ve vykonávání zbytku funkce, protože by mohlo dojít
        // k neočekávaným situacím kvůli rebootu zařízení.
        // Pro zařízení, která se nerebootovala je potřeba
        // nastavit novou verzi pro případné odeslání chyb.
        this.logger.setInfo({ version: this.session.version ?? undefined });
        return;
      }

      // TODO: Pokud přijde nový obsah a ten předchézí nebyl dokončen,
      // tak by bylo vhodné původní stahování zastavit.
      // Zatím je to řešeno takto jednoduše s frontou,
      // aby nedocházelo ke stahování různých obsahů současně.
      await this.applyNewContentMutex.enqueue(async () => {
        // Synchronizace obsahu s novým heartbeat timestamp.
        const rawContentSettings = await this.communicator
          .fetchContentSettings(
            this.contentInfo?.contentTimestamp ?? null,
            deviceId,
            heartbeatInfo.contentTimestamp,
            serverUrl
          )
          // Ošetření nevalidní odpověd. Vrácí se null, aby mohlo dojít k alespoň k updatování verze.
          .catch(() => null);

        if (isNil(rawContentSettings)) {
          // Pokud nebyl nový obsah stažen, tak není potřeba nic dělat.
          return;
        }

        // Naparsování nově příchozího ContentSettings.
        // Assety, kterým se liší checksum (typicky widgety), se zde nenamapují k mediím.
        // To vynutí stažení novější verze.
        const contentInfo = await ContentInfoParser.parse(
          this.logger,
          this.viewerRegistry,
          rawContentSettings,
          this.store,
          this.variablesNamespaceRegistry
        );
        if (heartbeatInfo.contentTimestamp !== contentInfo.contentTimestamp) {
          throw new Error('unexpected content timestamp from contentSettings');
        }

        // Stažení médií pro nový obsah.
        await this.contentDownloader.download(contentInfo, {
          downloadCallbacks: this.downloadCallbacks
        });

        // TODO: Ideální mít v transakci ukládání rawContentSettings a storage,
        // aby se případně předešlo nekonzistenci.

        // Po dokončení stahování medií uložím i stažené settings.
        await this.contentSettingsStore.put(rawContentSettings);
        // TODO: DRY, Device, ContentDownloader, ReportManager
        const contentInfoStorage =
          contentInfo.communicatorSettings.get('storage.types');
        // Hodnotou null je nechtěné si přepsat původně nastavenou hodnotu.
        if (!isNil(contentInfoStorage)) {
          // Aktualizace perzistentně ukládaných nastavení.
          await this.store.updateSession(this.session.id, {
            storage: contentInfoStorage
          });
        }

        // Aplikuje se ContentInfo.
        // Volání applyContent se neodmítne, takže není potřeba ošetřovat chybu.
        await this.replacer.applyContent(contentInfo);

        // Aplikuje se key/value nastavení.
        await this.setCustomSettings(contentInfo).catch(this.onError);
        // Zastaví se přehrávání obsahu
        await this.viewerManager.setContentInfo(null);
        // Dočasne pozastaví zber analytiky, ak bola zapnutá.
        this.analyticsTracker.enabled = false;

        // Informování zdrojů zobrazování o změně obsahu.
        // Zdroje tak mohou provést údržbu, když je jisté, že žádný není vypůjčený.
        await this.viewerRegistry
          .getAll()
          .reduce(
            (acc, viewerType) =>
              acc.then(() => viewerType.getSourceCreator().replace()),
            Promise.resolve()
          );

        // Po přijetí a stažení nového obsahu chceme resetovat uživatelskou neaktivitu.
        await this.resetIdleTime();

        // Jen pokud je analytika připravena (úspěšně namigrována), tak můžeme
        // zapnout sběr dat. Nesplnění očekávaného stavu je ošetřeno tím,
        // že se případně jednou za spuštění aplikace odešle výjimka na Sentry
        // v Device#start, aby se výjimky neposílaly opakovaně při každé změně
        // obsahu.
        if (this.analyticsInitialized) {
          this.analyticsTracker.enabled = contentInfo.analyticsEnabled;
        }
        // Spustí se ViewerManager s novým obsahem.
        // Chyba nezpůsobí zaseknutí procesu.
        await this.viewerManager
          .setContentInfo(contentInfo)
          .catch(this.onError);

        // Vyvolání události informujícím o novém obsahu.
        this.emitter.emit('content', contentInfo);

        // Přiřazení do this.contentInfo musí být až stažení a zobrazení nového obsahu,
        // aby při odeslání report info chodil starý contentTimestamp.
        // Nastaví se nový obsah.
        this.contentInfo = contentInfo;

        // Schválně se nečeká na výsledek, aby se zbytečně neblokoval
        // tok programu. V případě chyby se zaloguje.
        firstPlayingOccurred(this.viewerManager).then(() =>
          this.executeReportCommandWithoutError()
        );

        // Namigruje perzistentně uložené data do nové podoby.
        await migrate(this.store).catch(this.onError);
      });
    } catch (err) {
      this.onError(/** @type {Error} */ (err));
    }
  }

  /**
   * Resetuje čas uživatelské neaktivity.
   * @private
   * @returns {Promise<void>}
   */
  resetIdleTime() {
    return this.appManager.resetIdleTime().catch(noop);
  }

  /**
   * @private
   * @returns {Promise<void>}
   */
  async setAppInfo() {
    const { buildId, deviceId, fingerprint, serverUrl } = this.session;
    // Odfiltruje properties s null hodnotami.
    const appInfo = Object.entries({
      'app.connection.url': serverUrl,
      'app.deviceId': deviceId,
      'app.update.type.light.buildId': buildId,
      'app.fingerprint': fingerprint
    }).reduce((acc, [k, v]) => {
      if (v !== null) {
        acc[k] = v;
      }
      return acc;
    }, /** @type {Record<string, string>} */ ({}));
    await this.appManager.setAppInfo(appInfo);
  }

  /**
   * Předá platformě informace pro lepší identifikaci z běžící aplikace.
   * @private
   * @param {ContentInfo} contentInfo
   * @returns {Promise<void>}
   */
  async setCustomSettings(contentInfo) {
    /** @type {string | undefined} */
    // Chyba při získávání by neměla přijít. Pokud přijde,
    // tak to považujeme za nepodporovanou funkci.
    const customKey = await this.appManager
      .getCustomKey()
      .catch(() => undefined);
    // Pokud platforma nepodporuje získání custom key, tak se vrací undefined.
    // Takže volat setCustomSettings není povoleno.
    if (isNil(customKey)) {
      return;
    }
    const settings = contentInfo.communicatorSettings.getAll();
    const keyPrefix = `custom.${customKey}.`;
    const customSettings = Object.entries(settings).reduce(
      (acc, [key, value]) => {
        if (key.startsWith(keyPrefix)) {
          // Odstraní se prefix klíče.
          acc[key.slice(keyPrefix.length)] = value;
        }
        return acc;
      },
      /** @type {Record<string, string>} */ ({})
    );

    return await this.appManager.setCustomSettings(customSettings);
  }

  /**
   * @param {HeartbeatFetched} heartbeatFetched
   */
  setOffsetFromHeartbeat(heartbeatFetched) {
    const { fetchEndTimeMillis, heartbeatInfo } = heartbeatFetched;
    if (isNil(heartbeatInfo.currentTimeMillis)) {
      return;
    }
    this.unixTimestamp.setOffset(
      UnixTimestamp.calculateOffset(
        heartbeatInfo.currentTimeMillis,
        fetchEndTimeMillis
      )
    );
  }

  /**
   * @param {string} dateTimeParam
   * @returns {Promise<void>}
   * @throws {TypeError} Pokud je `dateTimeParam` nevalidní.
   */
  async shutdownWithWakeUpCommand(dateTimeParam) {
    const wakeUpTime = parseCommandDateTimeParam(dateTimeParam);
    await this.replacerPower.shutdownWithWakeUp(wakeUpTime);
  }

  // TODO: Spoustu nezávislých operací by chtělo paralelizovat pomocí Promise.all
  // (pokud se jednotlivé operace nemůžou odmítnout) nebo případně pomocí Promise.allSettled,
  // aby se snížil čas startu. Ale těžká věc je stanovit, které operace mají jaké závislosti.
  /** @type {Device['start']} */
  async start(contentInfo, el) {
    // Předá dodatečné informace běžící platformě.
    await this.setAppInfo().catch(this.onError);

    // Po spuštění chceme resetovat uživatelskou neaktivitu.
    await this.resetIdleTime();

    // Spuštění platformově specifického kódu.
    await this.startupManager.start().catch(this.onError);

    if (contentInfo) {
      await this.replacer.applyContent(contentInfo).catch(this.onError);

      // Aplikuje se key/value nastavení.
      await this.setCustomSettings(contentInfo).catch(this.onError);

      // Pokus o stažení chybějícího obsahu. Uvnitř je fronta,
      // takže nebude docházet k nepředvídatelným stavům.
      // Úmyslně se nečeká na výsledek, aby nebyl blokován tok programu.
      this.applyNewContentMutex
        .enqueue(async () => {
          const assets = await this.contentDownloader.download(contentInfo, {
            downloadCallbacks: this.downloadCallbacks
          });
          if (assets.length === 0) {
            return;
          }
          // Po dostahování medií je žádoucí odeslat reportInfo,
          // aby se aktualizoval počet nestažených assetů.
          // Schválně se nečeká na výsledek, aby se zbytečně neblokoval
          // tok programu. V případě chyby se zaloguje.
          this.executeReportCommandWithoutError();
        })
        .catch(this.onError);
    }

    // Kontrola informací o aktualizacích z minulého spuštění.
    await this.updater.start().catch(this.onError);

    // Zrušení starých již nepoužívaných zdrojů, aby nezabírali místo.
    await this.store.destroyAll(exceptionResource.name).catch(this.onError);

    // Pokud nemáme contentInfo, tak se analytika nezapíná.
    const analyticsEnabled = contentInfo?.analyticsEnabled ?? false;

    // Explicitní migrace analytických dat, která musí uspět, jinak se nesmí
    // zapnout analytika, jinak by se mohlo pracovat s nevalidními daty
    // a předpoklady! Po dokončení migrace nesmí ve Store zůstat žádné v1
    // skupiny (a jejich počítadla) a taktéž v2 skupiny (a počítadla) pro nesedící
    // aktuální serverUrl a deviceId. A samozřejmě ani osiřená počítadla.
    const warnings = /** @type {Error[]} */ ([]);
    await new AnalyticsMigrator(
      this.analyticsTransporter,
      this.session,
      this.store
    )
      .migrate(warnings)
      .then(() => {
        // Případné zapnutí sběru analytiky.
        this.analyticsTracker.enabled = analyticsEnabled;

        // TODO: Někde ve vypínání aplikace by měla být zaregistrovaná stop metoda.
        // Resp. mělo by existovat Device.stop(), kde by se to volalo. V opačném
        // případě pak volání start bez stop v testech způsobí úniku zdrojů a zejména
        // (při delším ladění nebo při použití fakeTimers) i možnost vyskočení něčeho,
        // co už se dávno nečeká!
        this.analyticsSender.start();
        this.analyticsInitialized = true;
      })
      .catch((cause) => {
        this.onError(
          new Error(
            'Analytics migration failed, disabling tracking and sending.',
            { cause }
          )
        );
      });

    if (warnings.length > 0) {
      this.onError(
        new AggregateError(
          warnings,
          'Analytics migration warnings (best-effort sending, data permanently deleted).'
        )
      );
    }
    this.viewerManager.setEl(el);
    await this.viewerManager.setContentInfo(contentInfo).catch(this.onError);

    this.contentInfo = contentInfo;

    // Před voláním musí být nastaveno this.contentInfo.
    // Výjimka by neměla nikdy nastat, protože metoda
    // start se volá vždy až po instalaci.
    await this.connect().catch(this.onError);

    // Počká se na zobrazení prvního obsahu a nebo maximálně 30 sekund,
    // aby se poslal nějaký report. Na volání se nečeká úmyslně.
    firstPlayingOccurred(this.viewerManager).then(() =>
      this.executeReportCommandWithoutError()
    );
  }

  /**
   * Zastaví přehrávání obsahu při čištění místa na disku.
   * @private
   * @returns {Promise<void>}
   */
  async stopContent() {
    await this.viewerManager.setContentInfo(null);
    // Nastaví se nový (prázdný) obsah.
    // Během této změny obsahu se neuvolňují zdroje, ani se nepřepínají
    // analytické intervaly, jako u běžné změny obsahu.
    // Také se tato změna neprojeví v "content" události.
    this.contentInfo = null;
  }

  // TODO: Bolo by chcené pred upgrade FW vymazať časovače a po úspechu zapnúť.
  /**
   * Aktualizace firmware.
   * Při úspěchu se záměrně zablokuje navždy (restart zařízení).
   * Může se zablokovat navždy i při nemožnosti zjištění chyby po zahájení aktualizace.
   * @returns {Promise<void>}
   * @throws {Error} Pokud není dostatek místa na disku pro stažení firmware.
   * @throws {Error & { name: 'InvalidStateError' }} Pokud je aktualizace firmware zakázána v nastavení.
   * @throws {Error & { name: 'NotFoundError' }} Pokud parser nenájde informace ze serveru o firmware.
   * @throws {Error & { name: 'NotSupportedError' }} Pokud není podporována aktualizace firmware pro platformu.
   */
  async upgradeFirmware() {
    if (isNil(this.contentInfo)) {
      return;
    }

    const disabled = CommunicatorFirmware.getFirmwareDisabled(
      this.contentInfo.communicatorSettings
    );
    if (disabled) {
      throw errorWithName(
        ErrorName.InvalidStateError,
        'Firmware upgrade is disabled by settings.'
      );
    }

    const manufacturerModel = await this.firmwareManager.getInfo();
    if (!isManufacturerModel(manufacturerModel)) {
      throw errorWithName(
        ErrorName.NotSupportedError,
        'Firmware info is not available: upgrade may not be possible.'
      );
    }

    const firmwareRelease = CommunicatorFirmware.getFirmwareRelease(
      this.contentInfo.communicatorSettings,
      manufacturerModel
    );
    if (!firmwareRelease) {
      throw errorWithName(
        ErrorName.NotFoundError,
        'Firmware release not found.'
      );
    }

    let size = config.firmware.defaultSize;
    // Pokus o získání skutečné velikosti z URL firmware pomocí HEAD.
    const fetchedSize = await tryFetchContentLength(firmwareRelease.url);
    if (validateFirmwareSize(fetchedSize)) {
      size = fetchedSize;
    } else if (validateFirmwareSize(firmwareRelease.defaultSize)) {
      size = firmwareRelease.defaultSize;
    }
    if (!(await this.hasInternalStorageSpace(size))) {
      // Ověříme, že je na disku dostatek místa
      // Firmware se stahuje do interního úložiště, proto je potřeba
      // kontrolovat místo v interním úložišti.
      throw new Error(
        `Insufficient space: at least ${size} B required on internal storage.`
      );
    }

    // Po úspěšné aktualizaci firmware se předpokládá restart zařízení.
    // Abychom neodemkli command mutex, tak proto schválně zablokujeme.
    await this.firmwareManager.update({ ...firmwareRelease, size });
    return new Promise(() => {});
  }
}

/**
 * Resolvuje se po přijetí prvního eventu playing nebo po uplynutí 30 s.
 * @param {ViewerManager} viewerManager
 * @returns {Promise<void>} Promise, která se vyřeší, když se poprvé spustí přehrávání obsahu.
 */
function firstPlayingOccurred(viewerManager) {
  const onPlaying = () => resolve(undefined);
  const { promise, resolve } = Promise.withResolvers();
  viewerManager.on('playing', onPlaying);
  return Promise.race([promise, PromiseUtil.wait(30 * 1000)]).finally(() =>
    viewerManager.off('playing', onPlaying)
  );
}

/**
 * Kontrola, zda je předaný objekt platný ManufacturerModel.
 * Navyše kontroluje, že všechny potřebné vlastnosti jsou vyplněné.
 * @param {Partial<FirmwareInfo>} partialFirmwareInfo
 * @returns {partialFirmwareInfo is ManufacturerModel}
 */
function isManufacturerModel(partialFirmwareInfo) {
  return (
    isString(partialFirmwareInfo.manufacturer) &&
    isString(partialFirmwareInfo.modelName)
  );
}

/**
 * @param {Url} url
 * @returns {Promise<Byte | undefined>}
 */
async function tryFetchContentLength(url) {
  try {
    const response = await fetchWithTimeout(url, { method: 'HEAD' });
    const contentLength = response.headers.get('Content-Length');
    if (response.ok && contentLength) {
      return Number.parseInt(contentLength, 10);
    }
  } catch (error) {
    // Pokud dojde k chybě, vrátí undefined.
    // Neloguje se, protože je velká šance, že se vyhodí výjimka.
  }
}

/**
 * Kontrola, zda je velikost pro stahování firmware platná.
 * @param {Byte | undefined} size
 * @returns {size is Byte}
 */
function validateFirmwareSize(size) {
  return !isNil(size) && Numeric.isIndex(size) && size > 0;
}
